import {GridRenderCellParams, useGridApiContext} from '@mui/x-data-grid-pro';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import React, {FC, MouseEvent, MouseEventHandler} from 'react';

import {Button} from '../../../../../Button';
import {Tooltip} from '../../../../../Tooltip';
import {CursorBox} from './CursorBox';
import {maybeGetDeletedRefValuePlaceholderFromRow} from './objectViewerDeletedRef';

const DEFAULT_INSET_SPACING = 40;

/**
 * Utility component for the ObjectViewer to allow expanding/collapsing of keys.
 */
export const ObjectViewerGroupingCell: FC<
  GridRenderCellParams & {
    insetSpacing?: number;
    onClick?: (event: MouseEvent) => void;
    onFilterClick?: (event: MouseEvent) => void;
  }
> = props => {
  const {id, field, rowNode, row} = props;
  const insetSpacing = props.insetSpacing ?? DEFAULT_INSET_SPACING;
  const isGroup = rowNode.type === 'group';
  const isExpandableRef = row.isExpandableRef;
  const apiRef = useGridApiContext();
  const onClick: MouseEventHandler<HTMLDivElement> = event => {
    if (isGroup) {
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
    }

    if (props.onClick) {
      props.onClick(event);
    }
  };

  const deletedRef = maybeGetDeletedRefValuePlaceholderFromRow(row);
  const tooltipContent = row.path.toString();
  const textContent = deletedRef ?? props.value;
  const box = (
    <TailwindContents>
      <CursorBox
        $isClickable={isGroup || isExpandableRef}
        onClick={onClick}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'left',
          width: '100%',
          '& .filter-button': {
            opacity: 0,
            transition: 'opacity 0.2s',
          },
          '&:hover .filter-button': {
            opacity: 1,
          },
        }}>
        {_.range(rowNode.depth).map(i => {
          return (
            <div
              key={i}
              className="flex h-full flex-col items-center justify-center"
              style={{
                flex: `0 0 ${insetSpacing / 2}px`,
                width: `${insetSpacing / 2}px`,
              }}
            />
          );
        })}
        <div
          className="flex h-full flex-row items-center justify-center"
          style={{
            flex: `0 0 ${insetSpacing}px`,
            width: `${insetSpacing}px`,
          }}>
          {isGroup || isExpandableRef ? (
            <Button
              variant="ghost"
              icon={
                isGroup && rowNode.childrenExpanded
                  ? 'chevron-down'
                  : 'chevron-next'
              }
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center pr-16">
              <div className="h-full w-full"></div>
              <div className="h-full w-full"></div>
            </div>
          )}
        </div>
        <div style={{flex: '1 1 100%'}}>
          <Tooltip
            content={tooltipContent}
            trigger={
              <div
                className="flex w-full flex-auto flex-row items-center justify-between overflow-hidden text-ellipsis whitespace-nowrap pr-8"
                style={{
                  textDecoration: deletedRef ? 'line-through' : 'none',
                }}>
                {textContent}
                {props.onFilterClick && (
                  <Button
                    className="filter-button"
                    variant="ghost"
                    size="small"
                    icon="filter-alt"
                    onClick={props.onFilterClick}
                  />
                )}
              </div>
            }
          />
        </div>
      </CursorBox>
    </TailwindContents>
  );

  return box;
};
